import {PopperModifierOverrides} from "@blueprintjs/core";

class Constants {
  static readonly DISPATCH_GROUP_LIMIT = 10000;
  static readonly DRIVER_LOCATION_LIMIT = 2000;
  static readonly DISPATCH_STATION_LIMIT = 10000;
  static readonly DISPATCH_DATA_LIMIT = 2000;
  static readonly DEFAULT_MANIFEST_PAGE_SIZE = 25;
  static readonly GENERAL_POLLING_INTERVAL = 15000;

  static readonly BATCH_OPTIMIZATION_POLLING_INTERVAL = 1000;
  static readonly BATCH_OPTIMIZATION_NUMBER_OF_STOPS = 100;

  static readonly MANIFEST_PAGE_SIZE_OPTIONS = [25, 50, 75];

  static readonly UNASSIGNED_JOB_STATUSES = ["N", "F", "I"];
  static readonly MAX_NUMBER_OF_CUSTOMERS_FILTERS = 10;

  static readonly RULE_REFRESH_INTERVAL = 5 * 60 * 1000;
  static readonly GOOGLE_API_KEY = "AIzaSyD6cEM1i3rSdP9CrHADl5giN8WOriKR13U";
  static readonly MANIFEST_NEW_AGE = 30 * 60;

  static readonly DEFAULT_MAX_CLOCK_SKEW_SECONDS = 600;
  static readonly CLOCK_SKEW_WARNING_THRESHOLD_MILLIS = Constants.DEFAULT_MAX_CLOCK_SKEW_SECONDS * 1000 * 0.8;

  static readonly NUMBER_OF_DETAILED_CARD_STOPS = 4;
  static readonly NUMBER_OF_COMPACT_CARD_STOPS = 3;

  static readonly MAX_HOUR_JOBS_FILTER = 99;
  static readonly MIN_HOUR_JOBS_FILTER = 0;

  static readonly MAX_NUMBER_OF_RETRY = 2;

  static readonly ARRIVAL_TIME_PRIOR_AT_LEAST = 1 * 60 * 1000;
  static readonly BROWSER_TIMEZONE_OFFSET_MINUTES = -new Date().getTimezoneOffset();

  static readonly MAX_UNREAD_MESSAGE_COUNT = 9;
  static readonly MANIFEST_DRAWER_RATIO_WIDTH = 60;
  static readonly GRAPHHOPPER_SOURCE = "dispatch";

  static readonly MeterToMileRatio = 0.000621371192;

  static readonly DATA_CHANGED_EVENTS_TTL = 10 * 60 * 1000;

  static readonly REGEX = {
    INVALID_FILE_NAME_REGEX: /[\\/:*?"<>|.]+/g,
    BAD_DATA_RESPONSE_REGEX: /Cannot return null for non-nullable type/i,
    INVALID_SEARCH_CHARACTER_REGEX: /[^\w\s-.]/,
    CODE_SIZE_LIMIT_REACHED_REGEX: /Reached evaluated resolver code size limit./,
    EMAIL_REGEX: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/
  };

  static readonly MOBILE_ETA_NOTIFICATION_CONFIG_KEY = "mobilejavamenuoptimizemanifest";

  static readonly ETA_PREMIUM_CONSULT_URL = "https://406zlk.share-na2.hsforms.com/2zdY0M-NsRNOaSD__FxQBiQ";

  static readonly ITINERARY_PLANNING_PREMIUM_CONSULT_URL =
    "https://406zlk.share-na2.hsforms.com/25WIvA6QNRx6-Ed93UDyKxA";

  static readonly MAP = {
    DEFAULT_ZOOM: 5,
    DEFAULT_CENTER: {lat: 37.0902, lng: -95.7129},
    ROUTE_COLORS: [
      "#2563EB",
      "#DC2626",
      "#16A34A",
      "#D97706",
      "#7C3AED",
      "#DB2777",
      "#0891B2",
      "#EA580C",
      "#65A30D",
      "#5900ad",
      "#0D9488",
      "#B45309",
      "#047857",
      "#C2410C",
      "#6D28D9",
      "#B7791F",
      "#064E3B",
      "#9D174D",
      "#0E7490",
      "#9A3412"
    ]
  };

  static readonly OPTIMIZATION = {
    LOCATION_TYPES: ["start", "end"],
    MAX_DWELL_MINUTES: 60,
    MIN_DWELL_MINUTES: 1,
    MAX_DRIVERS: 200,
    MAX_STOPS: 1100
  };

  static readonly CSV_IMPORT = {
    MAX_FILE_BYTES: 5 * 1024 * 1024,
    MAX_ORDERS: 500
  };
}

enum UserPreferences {
  jobPanelSize = "dispatch.ui.assignment.jobPanelSize",
  jobPanelMinimized = "dispatch.ui.assignment.jobPanelMinimized",
  mapPanelSize = "dispatch.ui.assignment.mapPanelSize",
  mapPanelMinimized = "dispatch.ui.assignment.mapPanelMinimized",
  manifestViewType = "dispatch.ui.assignment.manifestViewType",
  manifestFilterHeader = "dispatch.ui.manifest.filter.header",
  manifestDateRangeHeader = "dispatch.ui.manifest.date.range.header",
  manifestSortingHeader = "dispatch.ui.manifest.sorting.header",
  manifestGroupHeader = "dispatch.ui.manifest.group.header",
  manifestRowPerPage = "dispatch.ui.manifest.row.per.page",
  jobGridColumnState = "dispatch.ui.jobs.grid.columnState",
  jobGridFilterState = "dispatch.ui.jobs.grid.filterState",
  jobGridFilterReadyDue = "dispatch.ui.jobs.grid.filterReadyDueState",
  jobGridFilterReadyDueCustomValue = "dispatch.ui.jobs.grid.filterReadyDueState.customValue",
  jobGridFilterPlaced = "dispatch.ui.jobs.grid.filterPlacedState",
  isTimeFormat24hr = "dispatch.ui.app.settings.isTimeFormat24hr",
  isPresentationMode = "dispatch.ui.app.settings.isPresentationMode",
  dispatchStationsGroup = "dispatch.ui.app.settings.dispatchStationsGroup",
  mapActionsBarButton = "dispatch.ui.app.actions.mapActionsBarButton",
  manifestDetailGridColumnState = "dispatch.ui.manifest.detail.grid.columnState",
  manifestDetailGridFilterState = "dispatch.ui.manifest.detail.grid.filterState",
  manifestCardConfigurations = "dispatch.ui.manifest.card.configurations",
  manifestCardSortByColor = "dispatch.ui.manifest.card.sortByColor",
  manifestMapActionBar = "dispatch.ui.manifest.map.actionBar",
  assignedStopsGridColumnState = "dispatch.ui.assignedStops.grid.columnState",
  assignedStopsGridFilterState = "dispatch.ui.assignedStops.grid.filterState",
  assignedStopsGridReadyDueFilterState = "dispatch.ui.assignedStops.grid.readyDueFilterState",
  hideCmsUsernameHint = "dispatch.ui.userManagement.hideCmsUsernameHint"
}

enum TenantPreferences {
  driverRequirementEnforcement = "dispatch.ui.assignment.driverRequirementEnforcement",
  skillsBasedRequirements = "dispatch.ui.assignment.skillsBasedRequirements",
  orderEntryType = "dispatch.ui.assignment.orderEntryType",
  serviceTypeColor = "dispatch.ui.jobs.serviceTypeColor",
  dispatcherIgnoreTimeWindows = "dispatch.ui.routing.dispatcherIgnoreTimeWindows",
  driverIgnoreTimeWindows = "dispatch.ui.routing.driverIgnoreTimeWindows",
  dispatcherIgnoreTimeWindowsOnFirstTry = "dispatch.ui.routing.dispatcherIgnoreTimeWindowsOnFirstTry",
  dispatcherIgnoreTimeWindowsAsDefault = "dispatch.ui.routing.dispatcherIgnoreTimeWindowsAsDefault",
  assumedOptimizationDwellTime = "dispatch.ui.routing.driverAverageDwellTime",
  defineFirstStopAfterPickup = "dispatch.ui.routing.defineFirstStopAfterPickup",
  timezoneOfJobStop = "dispatch.ui.jobs.timezoneOfJobStop",
  requireArrAndDepTimes = "dispatch.ui.manifest.detail.requireArrAndDepTimes",
  unassignPartialJob = "dispatch.ui.manifest.detail.unassignPartialJob",
  vehicleIconSetup = "dispatch.ui.manifest.vehicleIconSetup",
  defaultManifestStatus = "dispatch.ui.manifest.defaultManifestStatus",
  numberOfUpcomingStops = "dispatch.ui.eta.numberOfUpcomingStops",
  assumedETADwellTime = "dispatch.ui.eta.assumedDwellTime",
  useEtaService = "dispatch.ui.eta.useEtaService",
  useGPSPoints = "dispatch.ui.eta.useGPSPoints",
  gpsFrequency = "dispatch.ui.eta.gpsFrequency",
  waitForEarlyTimeWindow = "dispatch.ui.eta.waitForEarlyTimeWindow",
  idexxCompleteStopLogic = "dispatch.ui.manifest.idexxCompleteStopLogic",
  askCheckoutTimeAndMilage = "dispatch.ui.manifest.askCheckoutTimeAndMileage",
  restrictStationVisibility = "dispatch.ui.assignment.restrictStationVisibility",
  createManifestOutSide = "dispatch.ui.manifest.createManifestOutSide",
  dismissFailedJobsRoutePlanning = "dispatch.ui.routePlanning.dismissFailedJobsRoutePlanning",
  optimizationObjectives = "dispatch.ui.routePlanning.optimizationObjectives",
  stopCompletedAtFutureTime = "dispatch.ui.manifest.detail.stopCompletedAtFutureTime",
  closedNeverReassign = "dispatch.ui.assignment.closedNeverReassign",
  routePlanningLimit = "dispatch.ui.routePlanning.limit",
  considerSkillsRoutePlanning = "dispatch.ui.routePlanning.considerSkills",
  mapSkillEnforcementRoutePlanning = "dispatch.ui.routePlanning.mapSkillEnforcement",
  suggestedAssignmentsPerDay = "dispatch.ui.assignment.suggestedAssignmentsPerDay",
  assignmentSubmittedNotification = "dispatch.ui.assignment.assignmentSubmittedNotification"
}

enum DateFormats {
  smallDate = "PP",
  longDate = "PPPP",
  monthDay = "LLL d",
  cellRendererDate = "EEE LLL dd yyyy",
  cellRendererTime = "h:mmaaa",
  osNow = "now",
  osISO = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
  localDate = "yyyy-MM-dd",
  localDateSlash = "MM/dd",
  awsDateTimeWithOffset = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
}

enum EmptyValueStrings {
  notAvailable = "N/A",
  notProvided = "Not Provided",
  unknown = "Unknown"
}

enum RouteOptimizationErrorCodes {
  TimeWindowError = 2,
  GroupRelationConstraint = 29,
  RelationContaint = 21
}

const OPTIMIZATION_LOCATION_TYPES = ["start", "end"];
const TIME_WINDOW_VALIDATIION_SEARCH_MESSAGE = ["Time windows mismatch:"];

enum OptimizedResultCords {
  OptimizedCords = "optimizedCords",
  OriginalCords = "originalCords",
  HomeCords = "homeCords"
}

enum ERR_WARNING_MESSAGES {
  BAD_DATA = "There is at least 1 manifest not showing because a stop from the manifest has an unsupported null data point"
}

enum MAP_OVERLAY_BREAK_POINTS {
  CHANGE_TO_COLUMN = "1305px",
  CHANGE_TO_SMALL_TEXT = "1200px"
}

enum ClockTimeFormat {
  TWELVE_HOUR = "12",
  TWENTY_FOUR_HOUR = "24"
}

const ClockTimeFormatLabels = {
  [ClockTimeFormat.TWELVE_HOUR]: "12 Hour Time",
  [ClockTimeFormat.TWENTY_FOUR_HOUR]: "24 Hour Time"
};

enum SaveAndClearJobStopDateTimeSource {
  USER = "user",
  SYSTEM = "system",
  NOT_SET = " "
}

const ClockTimeToolTipContent =
  "When manifest cards are in the minimal or compact mode, the time will always be 24 hour, to save space. All other parts of the application will show respect a 12 hour selection.";

const ALLOWED_SAVED_PREFERENCE_SOURCES = ["uiColumnMoved", "uiColumnResized", "toolPanelUi", "autosizeColumns"];

const COLORS = {
  BRIGHT_BLUE: "#F0F3FF",
  BLUE: "#2563EB",
  BLUE_LIGHT: "#93C5FD",
  BLUE_VERY_LIGHT: "#EFF6FF",
  BLUE_PALE: "#BFDBFE",
  BLUE_DARK: "#1D4ED8",
  DARK_BLUE: "#14305A",
  // DP-3098 — ETA notification feedback timeline (Figma Tailwind palette)
  BLUE_500: "#3B82F6",
  BLUE_100: "#DBEAFE",
  GRAY_800: "#1F2937",
  TEXT_BLACK: "#161616",
  TEXT_GRAY: "#797979",
  TEXT_GRAY_MEDIUM: "#6B7280",
  TEXT_RED: "#fa545e",
  WHITE: "#FFFFFF",
  BG_GRAY: "#EDEDED",
  BG_GRAY_LIGHT: "#F3F4F6",
  // DP-3260 — shaded rows in the job import tables, lighter than the BG_GRAY_LIGHT header
  BG_GRAY_LIGHTER: "#F9FAFB",
  BG_DISABLED: "#D9D9D9",
  BG_GRADIENT_DARK_BLUE: "linear-gradient(180deg, #214a89 0%, #14305a 100%)",
  COLOR_BORDER: "#D1D5DB",
  COLOR_BORDER_LIGHT: "#E5E7EB",
  COLOR_BORDER_DIVIDER: "#E0E0E0",
  COLOR_BORDER_SELECTED: "#AEC0FF",
  COLOR_BORDER_ERROR: "#CD4246",
  COLOR_PLACE_HOLDER: "#A1A1AA",
  GREEN_SUCCESS: "#32A467",
  SHADOW_BLACK: "#0000001a",
  LINK_BLUE: "#0000EE",
  // DP-3039 — "Unassigned" result in the Swap or Change Driver dialog (orange per Figma 4063-9863)
  TEXT_ORANGE: "#EA580C",
  // DP-3260 — job import confirmation panel (Tailwind orange-50 / orange-200 / red-100)
  ORANGE_SOFT: "#FFF7ED",
  ORANGE_BORDER: "#FED7AA",
  RED_SOFT: "#FEE2E2",
  // DP-3172 — tick circle background on the Itinerary Planning "in progress" screen
  GREEN_LIGHT: "#ECFDF3",
  // DP-3267 — negative suggestion chips; the light red of the existing warning/error popovers and toasts
  RED_LIGHT_BACKGROUND: "#ffdee0"
};

const DEFAULT_POPOVER_MODIFIERS: PopperModifierOverrides = {offset: {enabled: true, options: {offset: [0, 5]}}};

enum ETASourceType {
  COMPLETE_STOP = "completeStop",
  GPS_UPDATE = "gpsUpdate",
  OPTIMIZATION_SERVICE = "optimizationService",
  ROUTE_PLANNING = "routePlanning"
}

export {
  ALLOWED_SAVED_PREFERENCE_SOURCES,
  ClockTimeFormat,
  ClockTimeFormatLabels,
  ClockTimeToolTipContent,
  Constants,
  DateFormats,
  EmptyValueStrings,
  ERR_WARNING_MESSAGES,
  MAP_OVERLAY_BREAK_POINTS,
  OptimizedResultCords,
  RouteOptimizationErrorCodes,
  OPTIMIZATION_LOCATION_TYPES,
  TenantPreferences,
  TIME_WINDOW_VALIDATIION_SEARCH_MESSAGE,
  UserPreferences,
  COLORS,
  DEFAULT_POPOVER_MODIFIERS,
  SaveAndClearJobStopDateTimeSource,
  ETASourceType
};
